import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { User } from '../../../../@types/User';
import { useCompanyAttributionUsers } from '../../../../hooks/useCompanyAttributionUsers';
import type { RootState } from '../../../../store/store';
import { getSalaries } from '../../../../utils/api';
import { currentPlanningCycleYear } from '../../../../utils/boitageTimeline';
import { JWTHeader } from '../../../../utils/JWTHeader';
import { extractUniqueUsersFromFonctionResults } from '../../../../utils/users';
import { showToast } from '../../../common/Notification';
import InDevelopmentPopup from '../../../common/Popup/InDevelopmentPopup.module';
import { downloadPlanningPdf } from '../PlanningPdf';
import {
  ModalsContainer,
  ModuleCard,
  Tooltip,
  UserSection,
} from './components';
import { createUserSections } from './data';
import type {
  ModalState,
  Module,
  UserSection as UserSectionType,
} from './types';

interface ModulesOverviewProps {
  visibleSectionIds?: string[];
  expandSectionIds?: string[];
  isAdmin?: boolean;
  isLoading?: boolean;
  boitageEnabled?: boolean;
  restrictedToRowKeys?: string[];
  hideHeader?: boolean;
  defaultAllCollapsed?: boolean;
  flatMode?: boolean;
}

function ModulesOverview({ visibleSectionIds, expandSectionIds, isAdmin, isLoading, boitageEnabled = true, restrictedToRowKeys, hideHeader = false, defaultAllCollapsed = false, flatMode = false }: ModulesOverviewProps) {
  const navigate = useNavigate();
  
  const [tooltip, setTooltip] = useState<{ visible: boolean; content: string; x: number; y: number }>({
    visible: false,
    content: '',
    x: 0,
    y: 0,
  });

  // Create user sections and filter boitage-related modules when disabled
  const filterBoitageFromSections = React.useCallback((sections: UserSectionType[]): UserSectionType[] => {
    if (boitageEnabled) { return sections; }
    // Boitage-related module IDs to filter out
    const boitageModuleIds = ['building-selection', 'transaction-boitage', 'partner-boitage'];
    return sections.map((section) => ({
      ...section,
      modules: section.modules.filter((m) => !boitageModuleIds.includes(m.id)),
    }));
  }, [boitageEnabled]);

  const [userSections, setUserSections] = useState<UserSectionType[]>(
    filterBoitageFromSections(createUserSections(navigate)),
  );
  // Re-create sections when boitageEnabled changes
  React.useEffect(() => {
    setUserSections((prev) => {
      // Preserve collapsed state when re-filtering
      const collapsedMap = Object.fromEntries(prev.map((s) => [s.id, s.collapsed]));
      const fresh = filterBoitageFromSections(createUserSections(navigate));
      return fresh.map((s) => ({ ...s, collapsed: collapsedMap[s.id] ?? s.collapsed }));
    });
  }, [boitageEnabled, filterBoitageFromSections, navigate]);

  const [autoExpandKey, setAutoExpandKey] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (defaultAllCollapsed) { return; }
    let targetIds = (expandSectionIds && expandSectionIds.length > 0)
      ? expandSectionIds
      : (visibleSectionIds && visibleSectionIds.length > 0)
        ? visibleSectionIds
        : [];
    if (isAdmin && !targetIds.includes('referent')) {
      targetIds = ['referent', ...targetIds];
    }
    if (targetIds.length === 0) { return; }
    const key = [...targetIds].sort().join('|');
    if (autoExpandKey === key) { return; }
    setUserSections(prev => prev.map(section => ({
      ...section,
      collapsed: !targetIds.includes(section.id),
    })));
    setAutoExpandKey(key);
  }, [defaultAllCollapsed, expandSectionIds, visibleSectionIds, autoExpandKey, isAdmin]);

  const [modalState, setModalState] = useState<ModalState>({ 
    type: null, 
    sectionId: null, 
  });

  const [inDevPopup, setInDevPopup] = useState<{ open: boolean; title: string; description: string }>({
    open: false,
    title: '',
    description: '',
  });

  const { eventPositions, eventActivation, personAssignments, exactDates } = useSelector((state: RootState) => state.dashboard);
  const { company, jwt } = useSelector((state: RootState) => ({
    company: state.auth.company,
    jwt: state.auth.token,
  }));

  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const { attributionUsers } = useCompanyAttributionUsers(company?.id ?? null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  React.useEffect(() => {
    if (company?.id && jwt) {
      fetch(getSalaries(company.id, true), JWTHeader(jwt))
        .then((response) => response.json())
        .then((data) => {
          if (data?.results) {
            setCompanyUsers(
              extractUniqueUsersFromFonctionResults(
                data.results,
                (fonctionResult) => fonctionResult.company?.id === company.id,
              ),
            );
          }
        })
        .catch(() => {});
    }
  }, [company?.id, jwt]);

  const currentYear = useMemo(() => currentPlanningCycleYear(company?.toolsTimeline), [company?.toolsTimeline]);

  const savedTimelineData = company?.toolsTimeline?.[String(currentYear)];
  const startMonth = (typeof savedTimelineData?.startMonth === 'number') ? savedTimelineData.startMonth : 9;
  const savedRowVisibility = (savedTimelineData?.rowVisibility && typeof savedTimelineData.rowVisibility === 'object')
    ? savedTimelineData.rowVisibility as Record<string, boolean>
    : { 'referent-mails': true, 'sci-mails': true, 'ag-communication': true, 'property-communication': true, boitage: true };

  const handleDownloadReport = useCallback(async() => {
    if (isDownloadingReport) {return;}
    setIsDownloadingReport(true);
    try {
      await downloadPlanningPdf({
        companyName: company?.name ?? '',
        year: currentYear,
        startMonth,
        eventPositions,
        eventActivation,
        personAssignments,
        exactDates,
        rowVisibility: savedRowVisibility,
        companyUsers,
        referentSignataires: attributionUsers.referentSignataires,
        dpeQualificationManagers: attributionUsers.dpeQualificationManagers,
        agDocumentManagers: attributionUsers.agDocumentManagers,
      });
      showToast('success', 'Rapport téléchargé avec succès');
    } catch (error) {
      console.error('Error generating planning PDF:', error);
      showToast('error', 'Erreur lors de la génération du rapport');
    } finally {
      setIsDownloadingReport(false);
    }
  }, [isDownloadingReport, company?.name, currentYear, startMonth, eventPositions, eventActivation, personAssignments, exactDates, savedRowVisibility, companyUsers, attributionUsers]);

  const visibleSections = React.useMemo(() => {
    return userSections.filter((section) => isAdmin || !visibleSectionIds || visibleSectionIds.includes(section.id));
  }, [userSections, isAdmin, visibleSectionIds]);

  const getTotalModuleCount = () => {
    return visibleSections.reduce((total, section) => total + section.modules.length, 0);
  };

  // Event handlers
  const toggleUserSection = (sectionId: string) => {
    setUserSections(prev => 
      prev.map(section => 
        section.id === sectionId 
          ? { ...section, collapsed: !section.collapsed }
          : section,
      ),
    );
  };

  const openRoleModal = (sectionId: string) => {
    setModalState({ type: 'role', sectionId });
  };

  const closeModal = () => {
    setModalState({ type: null, sectionId: null });
  };

  const handleModuleClick = (module: Module) => {
    if (module.disabled) {
      setInDevPopup({
        open: true,
        title: module.title || 'Module en cours de développement',
        description: module.inDevMessage || 'Cette partie n’est pas encore disponible.',
      });
      return;
    }
    
    if (module.onClick) {
      module.onClick();
    } else {
      // No navigation provided → show in-development popup (e.g., Bibliothèque de documents items)
      setInDevPopup({
        open: true,
        title: module.title || 'Module en cours de développement',
        description: module.inDevMessage || 'Cette partie n’est pas encore disponible.',
      });
    }
  };

  const showTooltip = (event: React.MouseEvent, module: Module) => {
    if (module.disabled) {
      return;
    }

    const clientX = (event as React.MouseEvent).clientX;
    const clientY = (event as React.MouseEvent).clientY;

    setTooltip({
      visible: true,
      content: module.fullDesc,
      x: clientX + 16,
      y: clientY + 16,
    });
  };

  const hideTooltip = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const modulesContent = flatMode ? (
    <div className="flex flex-col gap-2.5">
      {visibleSections.flatMap((section) =>
        section.modules.map((module) => (
          <ModuleCard
            key={module.id}
            module={module}
            onModuleClick={handleModuleClick}
            onShowTooltip={showTooltip}
            onHideTooltip={hideTooltip}
            personAssignments={personAssignments}
            exactDates={exactDates}
            companyUsers={companyUsers}
            flatStandalone
          />
        )),
      )}
    </div>
  ) : (
    <div className="space-y-4">
      {visibleSections.map((section) => (
        <UserSection
          key={section.id}
          section={section}
          onToggleSection={toggleUserSection}
          onOpenRoleModal={openRoleModal}
          onModuleClick={handleModuleClick}
          onShowTooltip={showTooltip}
          onHideTooltip={hideTooltip}
          personAssignments={personAssignments}
          exactDates={exactDates}
          companyUsers={companyUsers}
          restrictedToRowKeys={section.id === 'referent' ? restrictedToRowKeys : undefined}
          headerAction={section.id === 'referent' ? (
            <button
              onClick={handleDownloadReport}
              disabled={isDownloadingReport}
              className={`group mb-2 flex w-full items-center gap-4 rounded-lg border-2 border-rgpd-blue/20 bg-gradient-to-r from-rgpd-blue/5 to-rgpd-blue/10 p-4 transition-all duration-300 hover:border-rgpd-blue/40 hover:shadow-lg ${
                isDownloadingReport ? 'cursor-wait opacity-70' : 'cursor-pointer hover:-translate-y-0.5'
              }`}
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rgpd-blue text-2xl shadow-md transition-transform duration-300 group-hover:scale-110">
                {isDownloadingReport ? (
                  <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-b-transparent" />
                ) : (
                  <span role="img" aria-label="chart">📊</span>
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-bold text-rgpd-blue">
                  {isDownloadingReport ? 'Génération en cours...' : 'Rapport de la Stratégie Croissance'}
                </div>
                <div className="text-xs text-gray-500">
                  Télécharger le rapport PDF du planning stratégique
                </div>
              </div>
              <div className="shrink-0 text-rgpd-blue/60 transition-transform duration-300 group-hover:translate-x-1">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
              </div>
            </button>
          ) : undefined}
        />
      ))}
    </div>
  );

  return (
    <>
      {flatMode ? (
        modulesContent
      ) : (
        <div className="relative mb-8 rounded-xl border border-white/20 bg-white p-8 shadow-xl transition-all duration-300 hover:shadow-2xl">
          <div className="absolute top-0 right-0 left-0 h-1 bg-rgpd-blue" />
          {!hideHeader && (
            <div className="mb-6 flex items-center gap-6">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-rgpd-blue/20 bg-rgpd-blue-light text-3xl shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                <span role="img" aria-label="target" className="drop-shadow-sm filter">🎯</span>
              </div>
              <div className="flex-1">
                {isLoading ? (
                  <div className="mb-2">
                    <Skeleton height={22} width={640} />
                  </div>
                ) : (
                  (() => {
                    const total = getTotalModuleCount();
                    return (
                      <h2 className="mb-2 text-xl leading-tight font-semibold text-gray-800">
                        {total > 0
                          ? `Accédez aux ${total} modules (organisés par type d'utilisateur) pour la mise en place de votre stratégie des 12 prochains mois`
                          : 'Accédez aux modules (organisés par type d\'utilisateur) pour la mise en place de votre stratégie des 12 prochains mois'}
                      </h2>
                    );
                  })()
                )}
                <p className="text-sm text-gray-600">Chaque service dispose de modules spécialement adaptés à ses besoins</p>
              </div>
            </div>
          )}
          <div className="overflow-visible opacity-100 transition-all duration-700 ease-in-out">
            {modulesContent}
          </div>
        </div>
      )}

      {/* Role/Mission Modal */}
      <ModalsContainer
        modalState={modalState}
        userSections={userSections}
        onCloseModal={closeModal}
      />

      {/* Tooltip */}
      <Tooltip tooltip={tooltip} />

      {/* In development popup */}
      <InDevelopmentPopup
        isOpen={inDevPopup.open}
        onClose={() => setInDevPopup({ ...inDevPopup, open: false })}
        title={inDevPopup.title}
        description={inDevPopup.description}
      />
    </>
  );
}

export default ModulesOverview;